'use client'

// LƯU TẠM KHI NÀO NEXT JS IMPROVE ĐƯỢC VẤN ĐỀ CHUYỂN TRANG THÌ CÂN NHẮC REMOVE COMPONENT NÀY
import Link, { type LinkProps } from 'next/link'
import { useRouter } from 'next/navigation'
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useTransition,
  type AnchorHTMLAttributes,
  type MouseEvent,
  type ReactNode
} from 'react'

// ============================================================================
// Navigation Pending Context & Provider
// ============================================================================

interface NavigationPendingContextType {
  isPending: boolean
  startNavigation: (callback: () => void) => void
}

const NavigationPendingContext = createContext<NavigationPendingContextType | null>(null)

interface NavigationPendingProviderProps {
  children: ReactNode
}

export function NavigationPendingProvider({ children }: NavigationPendingProviderProps) {
  const [isPending, startTransition] = useTransition()

  const startNavigation = (callback: () => void) => {
    const guard = window.__navigationGuard
    if (guard) {
      if (!confirm(guard)) return
      window.__navigationGuard = null
    }

    startTransition(() => {
      callback()
    })
  }

  return (
    <NavigationPendingContext.Provider value={{ isPending, startNavigation }}>
      {children}
    </NavigationPendingContext.Provider>
  )
}

export function useNavigationPending() {
  const context = useContext(NavigationPendingContext)
  if (!context) {
    throw new Error('useNavigationPending must be used within a NavigationPendingProvider')
  }
  return context
}

// ============================================================================
// Route Loading Overlay
// ============================================================================

function Spinner() {
  return (
    <div className="border-t-primary-600 h-8 w-8 animate-spin rounded-full border-2 border-gray-300" />
  )
}

function ReducedMotionSpinner() {
  return (
    <div className="border-t-primary-600 h-8 w-8 rounded-full border-2 border-gray-300 opacity-60" />
  )
}

interface RouteLoadingOverlayProps {
  children?: ReactNode
  LoadingComponent?: React.ComponentType<{ title?: string }>
  loadingTitle?: string
}

export function RouteLoadingOverlay({ LoadingComponent, loadingTitle }: RouteLoadingOverlayProps) {
  const { isPending } = useNavigationPending()
  const [showOverlay, setShowOverlay] = useState(false)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mediaQuery.matches)

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // Handle overlay visibility with delay
  useEffect(() => {
    if (isPending) {
      // Show overlay after 120ms delay to avoid flicker on fast transitions
      const timeout = setTimeout(() => {
        setShowOverlay(true)
      }, 120)

      return () => clearTimeout(timeout)
    } else {
      // Hide immediately when navigation completes
      setShowOverlay(false)
      return undefined
    }
  }, [isPending])

  if (!showOverlay) {
    return null
  }

  // If LoadingComponent is provided, use it instead of default spinner
  if (LoadingComponent) {
    return <LoadingComponent title={loadingTitle} />
  }

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center bg-white/70 dark:bg-black/40"
      aria-label="Loading navigation"
      role="status">
      {prefersReducedMotion ? <ReducedMotionSpinner /> : <Spinner />}
    </div>
  )
}

// ============================================================================
// NavLink Component
// ============================================================================

interface NavLinkProps {
  href: string
  children: ReactNode
  className?: string
  disabled?: boolean
  prefetchRoutes?: string[]
  onClick?: () => void
}

/**
 * NavLink component that uses startNavigation for programmatic navigation
 * with global loading overlay support
 */
export function NavLink({
  href,
  children,
  className = '',
  disabled = false,
  prefetchRoutes = [],
  onClick
}: NavLinkProps) {
  const router = useRouter()
  const { isPending, startNavigation } = useNavigationPending()

  // Prefetch specified routes on mount
  useEffect(() => {
    if (prefetchRoutes.length > 0) {
      prefetchRoutes.forEach((route) => {
        if (route) {
          router.prefetch(route)
        }
      })
    }
  }, [prefetchRoutes, router])

  const handleClick = () => {
    if (disabled || isPending || !href) {
      return
    }

    if (onClick) {
      onClick()
    }

    startNavigation(() => {
      router.push(href)
    })
  }

  return (
    <button
      type="button"
      className={`${className} ${isPending ? 'pointer-events-none opacity-60' : ''}`}
      onClick={handleClick}
      disabled={disabled || isPending}
      aria-label={typeof children === 'string' ? children : undefined}>
      {children}
    </button>
  )
}

// ============================================================================
// Enhanced Link Component - Wraps Next.js Link with Navigation Pending
// ============================================================================

/**
 * Enhanced Link component that wraps Next.js Link and automatically
 * triggers loading overlay on navigation. Use this instead of next/link
 * for better UX during page transitions in Next.js 15.
 *
 * Supports all props from Next.js Link and HTML anchor element including target, rel, etc.
 *
 * @example
 * ```tsx
 * <EnhancedLink href="/dashboard">Dashboard</EnhancedLink>
 * <EnhancedLink href="/external" target="_blank" rel="noopener noreferrer">External Link</EnhancedLink>
 * ```
 */
export function EnhancedLink({
  href,
  onClick,
  prefetch,
  className,
  children,
  target,
  ...props
}: LinkProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'onClick' | 'className'> & {
    prefetch?: boolean | null
    className?: string
    children?: ReactNode
  }) {
  const router = useRouter()
  const { isPending, startNavigation } = useNavigationPending()

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    // Let browser handle links with target="_blank", modifier keys, etc.
    if (
      target === '_blank' ||
      e.ctrlKey ||
      e.metaKey ||
      e.shiftKey ||
      (e.target as HTMLElement).getAttribute('target') === '_blank'
    ) {
      onClick?.(e)
      return
    }

    // Only intercept internal navigation
    const url = typeof href === 'string' ? href : href.pathname || ''
    if (url && !url.startsWith('http') && !url.startsWith('mailto:')) {
      e.preventDefault()
      if (isPending) {
        return
      }

      startNavigation(() => {
        // Cast href to match router.push's expected type
        router.push(href as Parameters<typeof router.push>[0])
      })
    }

    onClick?.(e)
  }

  return (
    <Link
      {...props}
      href={href}
      prefetch={prefetch}
      onClick={handleClick}
      target={target}
      className={`${className || ''} ${isPending ? 'pointer-events-none opacity-60' : ''}`.trim()}>
      {children}
    </Link>
  )
}

// ============================================================================
// useRouterWithNavigation Hook
// ============================================================================

/**
 * Hook that provides router methods wrapped with navigation pending state.
 * Use this instead of useRouter() when you need programmatic navigation
 * with loading overlay support.
 *
 * Supports both single parameter (App Router) and two parameters (Pages Router compatibility).
 * The second parameter is accepted for backward compatibility but ignored in App Router.
 *
 * @example
 * ```tsx
 * const router = useRouterWithNavigation()
 * router.push('/dashboard') // Shows loading overlay automatically
 * router.push('/dashboard', undefined) // Also works for backward compatibility
 * ```
 */
export function useRouterWithNavigation() {
  const router = useRouter()
  const { isPending, startNavigation } = useNavigationPending()

  return {
    ...router,
    isPending,
    push: (href: Parameters<typeof router.push>[0], as?: string | undefined) => {
      if (isPending) return
      startNavigation(() => {
        // In App Router, second parameter (as) is ignored, but we accept it for backward compatibility
        router.push(href)
      })
    },
    replace: (href: Parameters<typeof router.replace>[0], as?: string | undefined) => {
      if (isPending) return
      startNavigation(() => {
        // In App Router, second parameter (as) is ignored, but we accept it for backward compatibility
        router.replace(href)
      })
    }
  }
}
